/** One entry in Google Calendar's fixed event-color palette. */
export interface EventColor {
  /** Google Calendar event colorId ("1".."11"). */
  id: string;
  name: string;
  hex: string;
}

/**
 * Google Calendar's fixed event colors (the `colorId` palette). Google events can only use
 * one of these — there is no arbitrary hex for events — so we offer exactly this set.
 */
export const GOOGLE_EVENT_COLORS: EventColor[] = [
  { id: '1', name: 'Lavender', hex: '#7986cb' },
  { id: '2', name: 'Sage', hex: '#33b679' },
  { id: '3', name: 'Grape', hex: '#8e24aa' },
  { id: '4', name: 'Flamingo', hex: '#e67c73' },
  { id: '5', name: 'Banana', hex: '#f6bf26' },
  { id: '6', name: 'Tangerine', hex: '#f4511e' },
  { id: '7', name: 'Peacock', hex: '#039be5' },
  { id: '8', name: 'Graphite', hex: '#616161' },
  { id: '9', name: 'Blueberry', hex: '#3f51b5' },
  { id: '10', name: 'Basil', hex: '#0b8043' },
  { id: '11', name: 'Tomato', hex: '#d50000' },
];

export interface EventColorPickerProps {
  label?: string;
  /** Selected Google colorId, or null/'' for the calendar's default color. */
  value: string | null;
  onChange: (value: string | null) => void;
  className?: string;
}

/** A swatch picker for Google Calendar event colors, plus a "Calendar default" option. */
export function EventColorPicker({ label, value, onChange, className = '' }: EventColorPickerProps) {
  const selected = value || '';
  return (
    <div className={`mb-2 ${className}`.trim()}>
      {label && <label className="form-label d-block">{label}</label>}
      <div className="d-flex flex-wrap align-items-center gap-2">
        <button
          type="button"
          className="btn btn-sm rounded-pill border d-inline-flex align-items-center gap-1"
          style={{ background: selected === '' ? '#e2e8f0' : 'transparent' }}
          aria-pressed={selected === ''}
          onClick={() => onChange(null)}
        >
          {selected === '' && <i className="ph-check" style={{ fontSize: 12 }} />}
          Calendar default
        </button>
        {GOOGLE_EVENT_COLORS.map((c) => {
          const active = selected === c.id;
          return (
            <button
              key={c.id}
              type="button"
              className="d-inline-flex align-items-center justify-content-center"
              aria-label={c.name}
              aria-pressed={active}
              title={c.name}
              onClick={() => onChange(c.id)}
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: c.hex,
                border: active ? '2px solid #0f172a' : '2px solid transparent',
                boxShadow: active ? '0 0 0 2px #fff inset' : 'none',
                cursor: 'pointer',
                color: '#fff',
              }}
            >
              {active && <i className="ph-check" style={{ fontSize: 14 }} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
